import { apiGet } from './client';

export type UsagePeriod = '7d' | '30d' | 'mtd';

export interface UsageByFeature {
  feature: string;
  cost_usd: number;
  calls: number;
}

export interface UsageDaily {
  date: string;
  cost_usd: number;
}

export interface AiUsageSummary {
  period: UsagePeriod;
  total_cost_usd: number;
  total_tokens: number;
  by_feature: UsageByFeature[];
  daily: UsageDaily[];
}

/** AI spend + token usage for the current tenant. Reads the existing
 *  services/ai `GET /ai/usage/summary` aggregation (tenant-scoped via x-tenant-id). */
export async function getAiUsageSummary(period: UsagePeriod = '30d'): Promise<AiUsageSummary> {
  return apiGet<AiUsageSummary>(`/ai/usage/summary?period=${period}`);
}

export interface AiBudget {
  is_over_limit: boolean;
  /** True when billing was unreachable — don't show a misleading warning in that case. */
  degraded: boolean;
  ai_used_usd: number;
  ai_limit_usd: number;
  ai_staff_assisted_usd: number;
  /** Customer-billable tokens used this calendar month (for the non-root usage view). */
  tokens_used: number;
  plan: string | null;
}

/** The tenant's customer-billable AI spend this month vs their plan's AI limit, for the usage
 *  warning banner. Staff-assisted usage is excluded (billed separately). */
export async function getAiBudget(): Promise<AiBudget> {
  return apiGet<AiBudget>('/builder/ai-budget');
}

export interface SiteAiSpend {
  site_id: string;
  /** Customer-billable AI credits spent on THIS site, all-time (staff-assisted excluded). */
  site_used_usd: number;
}

/** Total AI credits spent on the ACTIVE site (the one in the X-Site-Id header the client attaches),
 *  for the wizard's per-site "spent so far" counter — resets per site, unlike getAiBudget's
 *  account-wide total. */
export async function getSiteAiSpend(): Promise<SiteAiSpend> {
  return apiGet<SiteAiSpend>('/builder/ai-spend');
}
